// src/components/CustomHeader.tsx
import React, { useEffect } from 'react';
import { Box, Avatar, Badge, Tooltip, } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { getUserRoleName } from '@/utils/helpers';
import { AppDispatch, RootState } from '@/stores';
import { fetchNotificationCount } from '@/stores/notificationSlice';
import { StyledAppBar, StyledContainer, HeaderBox, NotificationIconButton, UserBox, AvatarWrapper, UserInfoBox, UserNameTypography, UserRoleTypography, } from './styles/CustomUserHeader.styles';
import NotificationModal from './UI/NotificationListModal';
import { openDialerForUser } from '@/utils/dialer';


interface CustomUserHeaderProps {
  onMenuClick?: () => void;
}

const CustomHeader: React.FC<CustomUserHeaderProps> = ({ onMenuClick }) => {
  const {
    user,
    isAgent,
    isDepartmentManager,
    isGroupUser,
    isSalesUser,
    isSystemAdmin,
  } = useRolePermissions();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const count = useSelector((state: RootState) => state.notifications.count);
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const showAgentTypeSuffix = isAgent && user && 'agentType' in user && user.agentType;
  const showDialerAction = !isGroupUser;
  const showTicketAction = isSystemAdmin || isDepartmentManager || isAgent;
  // Sales users (sales agents on agentRoutes, sales managers on superVisorRoutes)
  // work deals rather than tickets, so the header action points them at the
  // sales pipeline. Both route sets expose it at the same '/crm/sales' path.
  const ticketActionLabel = isSalesUser ? 'Open sales - Deals' : 'Open tickets';
  const ticketActionPath = isSalesUser ? '/crm/sales' : '/tickets';

  useEffect(() => {
    dispatch(fetchNotificationCount());
  }, [dispatch]);


  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const notificationClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null)


  return (
    <StyledAppBar position="static" elevation={0}>
      <StyledContainer maxWidth={false} disableGutters>
        <HeaderBox>
          {onMenuClick && (
            <Tooltip title="Open navigation">
              <NotificationIconButton
                aria-label="Open navigation"
                onClick={onMenuClick}
                sx={{ mr: { xs: 0.75, sm: 1.75 } }}
              >
                <MenuIcon />
              </NotificationIconButton>
            </Tooltip>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', minWidth: 0 }}>
            {showDialerAction && (
              <Tooltip title="Open dialer">
                <NotificationIconButton
                  className="blue"
                  aria-label="Open dialer"
                  onClick={() => void openDialerForUser(user, accessToken)}
                >
                  <img src="/images/call-icon.svg" alt="Call" />
                </NotificationIconButton>
              </Tooltip>
            )}
            {showTicketAction && (
              <Tooltip title={ticketActionLabel}>
                <NotificationIconButton
                  className="yellow"
                  aria-label={ticketActionLabel}
                  onClick={() => navigate(ticketActionPath)}
                >
                  <img src="/images/email-icon.svg" alt="Msg" />
                </NotificationIconButton>
              </Tooltip>
            )}
            <NotificationIconButton
              // onClick={() => navigate('/notifications') }
              aria-label="Open notifications"
              onClick={notificationClick}
            >
              <Badge badgeContent={count} color="primary">
                <img src="/images/notification-icon2.svg" alt="bell" />
              </Badge>
            </NotificationIconButton>
            <NotificationModal onClose={handleClose} anchorEl={anchorEl} />
            <UserBox onClick={() => navigate('/profile')} sx={{ cursor: 'pointer' }}>
              <AvatarWrapper>
                <Avatar
                  alt={user?.fullName || user?.userName || "User"}
                  src={(user as any)?.profileImage}
                  sx={{ width: 38, height: 38 }}
                >
                  {!(user as any)?.profileImage && (user?.fullName || user?.userName || 'U').charAt(0).toUpperCase()}
                </Avatar>
              </AvatarWrapper>
              <UserInfoBox>
                <UserNameTypography>{user?.fullName || user?.userName || "Guest"}</UserNameTypography>
                <UserRoleTypography>
                  {getUserRoleName(user)}
                  {showAgentTypeSuffix ? ` - ${user.agentType}` : ''}
                </UserRoleTypography>
              </UserInfoBox>
            </UserBox>
          </Box>
        </HeaderBox>
      </StyledContainer>
    </StyledAppBar>
  );
};

export default CustomHeader;
